import * as React from 'react';
import axios from '../../utils/axios';

/* ============================================================================
   Ícone do aplicativo, vindo do managed Google Play.

   O ícone não está no nosso banco: a tabela de aplicativos guarda nome, pacote
   e status. Quem tem a arte é o Google, em `enterprises.applications.get` — a
   mesma consulta que passou a preencher o nome ao escolher na loja.

   TRÊS CUIDADOS, porque isto roda numa lista:

   1. CACHE EM DOIS NÍVEIS: memória (a mesma página) e `localStorage` (sobrevive
      a F5, aba nova e ao dia seguinte), com validade de 7 dias. Ver o bloco
      logo abaixo.

   2. SÓ O QUE ESTÁ NA TELA. O componente busca no primeiro render de cada
      linha, então são ~11 consultas por página, uma vez por pacote por sessão -
      e não 795 de uma vez.

   3. FALHA EM SILÊNCIO, sem espaço reservado. App que só existe na loja
      pública, cota estourada, rede: nesses casos não aparece nada. Um
      quadradinho cinza de "sem ícone" repetido em metade das linhas é pior que
      linha nenhuma - vira ruído com a mesma forma do dado.

   Depende do endpoint `/aplicativos/google/:pacote`, que é novo: enquanto a API
   em produção não for atualizada, a consulta dá 404, cai no catch e a lista
   fica exatamente como está hoje.
   ============================================================================ */

/* ============================================================================
   CACHE EM DOIS NÍVEIS (11/09/2026, a pedido: "não carregar toda hora").

   O cache de memória sozinho resolvia a mesma sessão, mas morria a cada F5 e a
   cada aba nova — e a lista de aplicativos é justamente uma tela de voltar
   toda hora. Onze consultas ao Google por página, repetidas o dia inteiro.

   1. MEMÓRIA (`Map`): resolve a mesma página e o mesmo render. Guarda a
      PROMESSA, não o resultado, para que dez linhas do mesmo pacote
      compartilhem UMA requisição em vez de dispararem dez.

   2. `localStorage`: sobrevive a recarga, a aba nova e ao dia seguinte.

   VALIDADE DE 7 DIAS. Ícone de aplicativo praticamente não muda; quando muda,
   esperar até uma semana pela arte nova não custa nada a ninguém. Sem validade,
   um ícone trocado ficaria errado para sempre.

   O "NÃO TEM ÍCONE" TAMBÉM É GUARDADO, e é metade do ganho: aplicativo que só
   existe na loja pública nunca vai ter ícone, e sem registrar a ausência ele
   seria consultado de novo a cada abertura, para receber o mesmo nada.

   TUDO EM TRY/CATCH porque `localStorage` lança em aba anônima, com cookies
   bloqueados e quando a cota estoura. Cache que derruba a tela é pior que
   cache nenhum — na falha, cai para o comportamento de antes.
   ============================================================================ */

/** pacote -> promessa da URL do ícone (ou null quando não há). */
const cache = new Map<string, Promise<string | null>>();

const PREFIXO = 'gpshub:icone:';
const VALIDADE_MS = 7 * 24 * 60 * 60 * 1000;

type Guardado = { url: string | null; quando: number };

function lerDoDisco(pacote: string): Guardado | null {
  try {
    const bruto = localStorage.getItem(PREFIXO + pacote);
    if (!bruto) return null;
    const dado = JSON.parse(bruto) as Guardado;
    if (!dado || typeof dado.quando !== 'number') return null;
    if (Date.now() - dado.quando > VALIDADE_MS) {
      localStorage.removeItem(PREFIXO + pacote);
      return null;
    }
    return dado;
  } catch {
    return null;
  }
}

function gravarNoDisco(pacote: string, url: string | null) {
  try {
    localStorage.setItem(PREFIXO + pacote, JSON.stringify({ url, quando: Date.now() }));
  } catch {
    /* Cota estourada é o caso comum aqui: 795 aplicativos x ~120 bytes ainda
       cabem com folga nos 5 MB do navegador, mas o espaço é compartilhado com
       o resto do sistema. Limpa as entradas de ícone e desiste desta - o cache
       de memória continua valendo para esta sessão. */
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith(PREFIXO))
        .forEach((k) => localStorage.removeItem(k));
    } catch {
      /* nem limpar deu: segue sem cache em disco */
    }
  }
}

function buscarIcone(pacote: string): Promise<string | null> {
  const emMemoria = cache.get(pacote);
  if (emMemoria) return emMemoria;

  const emDisco = lerDoDisco(pacote);
  if (emDisco) {
    const pronta = Promise.resolve(emDisco.url);
    cache.set(pacote, pronta);
    return pronta;
  }

  const promessa = axios
    .get<{ iconUrl?: string | null }>(`/aplicativos/google/${encodeURIComponent(pacote)}`)
    .then((r) => {
      const url = r.data?.iconUrl || null;
      gravarNoDisco(pacote, url);
      return url;
    })
    .catch(() => {
      /* NÃO grava a falha em disco. Erro de rede ou API fora do ar é
         temporário: registrar isso por 7 dias esconderia o ícone muito depois
         de o problema ter passado. O `null` de "não tem ícone" vem do caminho
         de sucesso acima, e esse sim é guardado. */
      return null;
    });

  cache.set(pacote, promessa);
  return promessa;
}

export default function IconeAplicativo({
  pacote,
  nome,
  tamanho = 20,
}: {
  pacote?: string | null;
  nome?: string | null;
  tamanho?: number;
}) {
  const [url, setUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!pacote) return undefined;
    let vivo = true;
    buscarIcone(pacote).then((u) => {
      if (vivo) setUrl(u);
    });
    return () => {
      vivo = false;
    };
  }, [pacote]);

  if (!url) return null;

  return (
    <img
      src={url}
      /* `alt` vazio e `aria-hidden`: o nome do app está escrito ao lado, em
         texto. Repetir "ícone do Pontomais" faz o leitor de tela anunciar a
         mesma coisa duas vezes por linha. */
      alt=""
      aria-hidden
      width={tamanho}
      height={tamanho}
      loading="lazy"
      className="flex-none rounded-[5px] bg-surface-2 object-cover"
      style={{ width: tamanho, height: tamanho }}
      /* Ícone que responde 404 depois de a URL vir some, em vez de deixar o
         quadrado quebrado do navegador. */
      onError={() => setUrl(null)}
      title={nome || pacote || undefined}
    />
  );
}
